import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { ChevronRight, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const { handleAuthResponse } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password, remember_me: rememberMe },
        { withCredentials: true }
      );

      handleAuthResponse(response.data);
      const dashboardPath = getDashboardPath(response.data.user.role);
      navigate(dashboardPath, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Hero Image & Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image - Chiefs Helmet */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('/chiefs-helmet.png')`,
          }}
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/60" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-end p-12 w-full h-full">
          {/* Top - Logo */}
          <div className="absolute top-12 left-12">
            <img 
              src="/chiefs-logo-dark.png" 
              alt="Park City Chiefs" 
              className="w-48 h-auto object-contain drop-shadow-2xl"
            />
          </div>
          
          {/* Bottom - The Tribe Standard */}
          <div className="mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-4">
              Welcome Back
            </p>
            <h1 className="font-display text-5xl xl:text-6xl font-black text-white uppercase tracking-tighter leading-none mb-6 drop-shadow-lg">
              THE TRIBE <span className="text-red">STANDARD</span>
            </h1>
            <div className="text-white text-lg max-w-md leading-relaxed font-body space-y-2 drop-shadow-md">
              <p className="italic">More than a league, <span className="font-semibold">WE are family.</span></p>
              <p>Structure. Mentorship. Academic accountability.</p>
              <p className="text-white/80">Where development comes before everything else.</p>
              <p className="mt-4 font-semibold">
                Join The Tribe. <span className="text-gold">Build the Future.</span>
              </p>
            </div>
          </div>
          
          {/* Bottom - Core Values */}
          <div className="flex items-center space-x-3 text-white/60 text-xs font-medium uppercase tracking-wider">
            <span>Tribe</span>
            <span className="w-1 h-1 bg-gold rounded-full"></span>
            <span>Family</span>
            <span className="w-1 h-1 bg-gold rounded-full"></span>
            <span>Discipline</span>
            <span className="w-1 h-1 bg-gold rounded-full"></span>
            <span>Development</span>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-[#F4F6F9]">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img 
                src="/chiefs-logo.png" 
                alt="Park City Chiefs" 
                className="w-48 h-auto object-contain"
              />
            </div>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="mb-6">
              <h2 className="font-display text-2xl font-black text-navy uppercase tracking-tighter mb-1">
                Sign In
              </h2>
              <p className="text-gray-500 text-sm font-body">Access your team portal</p>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-4 border-red/30 bg-red/5" data-testid="login-error-alert">
                <AlertDescription data-testid="login-error-message" className="text-red font-medium">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-gray-600">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="mt-2 h-12 bg-gray-50 border-gray-200 rounded-lg focus:border-light-blue focus:ring-2 focus:ring-light-blue/20 transition-all duration-200"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-gray-600">
                  Password
                </Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="h-12 bg-gray-50 border-gray-200 rounded-lg focus:border-light-blue focus:ring-2 focus:ring-light-blue/20 transition-all duration-200 pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    data-testid="login-toggle-password"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember-me"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked)}
                  data-testid="login-remember-me"
                  className="border-gray-300 data-[state=checked]:bg-navy data-[state=checked]:border-navy"
                />
                <Label 
                  htmlFor="remember-me" 
                  className="text-sm text-gray-600 cursor-pointer"
                >
                  Remember me for 30 days
                </Label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-red hover:bg-red/90 text-white font-bold uppercase tracking-wider rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing In...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    Sign In
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </span>
                )}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="px-3 bg-white text-gray-400 font-medium tracking-wider">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              className="w-full h-12 border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-lg font-medium transition-all duration-200"
              data-testid="google-login-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Google
            </Button>
          </div>

          {/* Sign up link */}
          <p className="mt-6 text-center text-sm text-gray-600">
            New to Park City Chiefs?{' '}
            <Link 
              to="/signup" 
              className="text-navy font-bold hover:text-red transition-colors duration-200" 
              data-testid="signup-link"
            >
              Join the Tribe
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

function getDashboardPath(role) {
  if (role === 'super_admin' || role === 'league_admin') {
    return '/admin/dashboard';
  } else {
    return '/dashboard';
  }
}

export default Login;
